import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BookOpen, ChevronRight, Layers, Percent } from 'lucide-react';
import { groups, modesInGroup, topModes, type GroupDef, type ModeDef } from '@/config/modes.config';
import { levels } from '@/config/levels.config';
import { REGIONS } from '@/engine';
import { RangeMatrix } from '@/ui/RangeMatrix';
import { ComboCards } from '@/ui/ComboCards';
import { comboCount } from '@/engine/combos';
import { useSettings } from '@/store/settings';
import { HomeLayout } from './HomeLayout';
import { ChipPie, TEMPLATES } from './Morphology';
import { readableChapters } from '@/config/book.config';

export function HomeScreen() {
  const { t } = useTranslation();
  return (
    <HomeLayout section={t('home.modes')}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] gap-6">
        {groups.map((group, i) => (
          <GroupCard key={group.id} group={group} index={i} />
        ))}
        {topModes.map((mode, i) => (
          <ModeCard key={mode.id} mode={mode} index={groups.length + i} />
        ))}
        <SoonCard
          ns="foldEquity"
          to="/fold-equity"
          icon={Percent}
          index={groups.length + topModes.length}
        />
        <SoonCard
          ns="texture"
          to="/texture"
          icon={Layers}
          index={groups.length + topModes.length + 1}
        />
        <BookCard index={groups.length + topModes.length + 2} />
        <MorphologyCard index={groups.length + topModes.length + 3} />
      </div>
    </HomeLayout>
  );
}

function Card({
  label,
  index,
  to,
  thumb,
  children,
}: {
  label: string;
  index: number;
  to: string;
  thumb: React.ReactNode;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const go = (): void => {
    void navigate(to);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="enter group relative flex min-h-[220px] cursor-pointer flex-col gap-3.5 overflow-hidden rounded-[20px] border-2 border-accent bg-surface p-7 pb-6 text-left transition-transform hover:-translate-y-[3px]"
      style={{ boxShadow: 'var(--glow)', animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="font-display text-[26px] leading-[1.1] font-extrabold uppercase italic text-accent">
          {label}
        </span>
        <div
          className="terms shrink-0"
          style={{ '--cell': '6px', '--thumb': '6px' } as React.CSSProperties}
          aria-hidden
        >
          {thumb}
        </div>
      </div>
      {children}
    </div>
  );
}

function CardFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-auto flex items-center justify-between gap-2.5 border-t border-line pt-4">
      <span className="label">{children}</span>
      <ChevronRight
        className="size-5 text-fg-dim transition-transform group-hover:translate-x-0.5 group-hover:text-accent"
        strokeWidth={2.4}
        aria-hidden
      />
    </div>
  );
}

/** One card per mode; opens the mode's level cards. */
export function ModeCard({ mode, index }: { mode: ModeDef; index: number }) {
  const { t } = useTranslation();
  const size = REGIONS[mode.region].length;
  return (
    <Card
      label={t(`mode.${mode.id}.name`)}
      index={index}
      to={`/m/${mode.id}`}
      thumb={<RangeMatrix b={Math.ceil(size / 2)} region={mode.region} static />}
    >
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">{t(`mode.${mode.id}.desc`)}</p>
      <CardFooter>
        {t('home.hands', { count: size })} · B {mode.b.min}–{mode.b.max} ·{' '}
        {t('home.levelsCount', { count: levels.length })}
      </CardFooter>
    </Card>
  );
}

/** One card per group; opens the group's mode cards. The thumbnail is a combo grid with the group's blocked lines. */
function GroupCard({ group, index }: { group: GroupDef; index: number }) {
  const { t } = useTranslation();
  const count = modesInGroup(group.id).length;
  const first = modesInGroup(group.id)[0]?.id ?? 'range';
  const custom = useSettings((s) => s.blockersFor(first, levels[0]!.id));
  const b = group.blockers === 'custom' ? custom : group.blockers;
  const rows = Array.from({ length: b.rows === 'random' ? 1 : b.rows }, (_, i) => i);
  const cols = Array.from({ length: b.cols === 'random' ? 1 : b.cols }, (_, i) => 3 - i);
  return (
    <Card
      label={t(`group.${group.id}.name`)}
      index={index}
      to={`/g/${group.id}`}
      thumb={
        <ComboCards
          a={comboCount('both', rows, cols)}
          side="both"
          blockedRows={rows}
          blockedCols={cols}
        />
      }
    >
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">{t(`group.${group.id}.desc`)}</p>
      <CardFooter>
        {t('home.modesCount', { count })} · {t('home.levelsCount', { count: levels.length })}
      </CardFooter>
    </Card>
  );
}

/** the book summaries as a card */
function BookCard({ index }: { index: number }) {
  const { t } = useTranslation();
  return (
    <Card
      label={t('book.title')}
      index={index}
      to="/book"
      thumb={<BookOpen className="size-[84px] text-accent opacity-80" strokeWidth={1.6} />}
    >
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">{t('book.desc')}</p>
      <CardFooter>{t('book.chaptersCount', { count: readableChapters.length })}</CardFooter>
    </Card>
  );
}

/** the Morphology page as a card; the thumbnail is a chip with four equal slices */
function MorphologyCard({ index }: { index: number }) {
  const { t } = useTranslation();
  const thumb = [1, 2, 3, 4].map((id) => ({ id, name: '', value: 1 }));
  return (
    <Card
      label={t('morphology.title')}
      index={index}
      to="/morphology"
      thumb={<ChipPie items={thumb} labels={false} className="size-[84px]" />}
    >
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">{t('morphology.desc')}</p>
      <CardFooter>
        {TEMPLATES.length} {t('morphology.templates')}
      </CardFooter>
    </Card>
  );
}

/** A coming-soon exercise (fold equity, texture): opens its placeholder screen until the client defines it. */
function SoonCard({
  ns,
  to,
  icon: Icon,
  index,
}: {
  ns: 'foldEquity' | 'texture';
  to: string;
  icon: typeof Percent;
  index: number;
}) {
  const { t } = useTranslation();
  return (
    <Card
      label={t(`${ns}.title`)}
      index={index}
      to={to}
      thumb={<Icon className="size-[84px] text-accent opacity-80" strokeWidth={1.6} />}
    >
      <p className="m-0 max-w-[40ch] text-[13.5px] leading-[1.6]">{t(`${ns}.desc`)}</p>
      <CardFooter>{t(`${ns}.soon`)}</CardFooter>
    </Card>
  );
}
